import * as Sentry from '@sentry/react';
import React from 'react';
import { Stack, Button, Modal } from '@mui/material';
import GetAppIcon from '@material-ui/icons/GetApp';
import { DropzoneArea } from 'material-ui-dropzone';
import { CSVLink } from 'react-csv';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { Projection } from './types';
import { useDispatch } from 'react-redux';
import { setPlayerExposureCaps } from '../store/actions';
import { SlateResponse } from '../store/types';

interface OwnProps {
  readonly slateId: string;
  readonly onSetProj: (customProjections: Projection[]) => void;
  readonly title?: string;
  readonly selectedSlate?: SlateResponse;
}

type Props = OwnProps;

const ProjectionModal = (props: Props) => {
  const { slateId, title, onSetProj, selectedSlate } = props;
  const [open, setOpen] = React.useState(false);

  const handleSubmit = (customProjections: Projection[]) => {
    onSetProj(customProjections);
    setOpen(false);
  }
  return (
    <>
      <Modal
        open={open}
        onClose={(event: object, reason: string) => {
          if (reason?.toLowerCase() === 'backdropclick') {
            setOpen(false)
          }
        }}
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <>
          <ModalBox slateId={slateId} selectedSlate={selectedSlate} onSetProj={(customProjections: Projection[]) => handleSubmit(customProjections)} />
        </>
      </Modal>
      <Stack marginRight="20px" direction="row" justifyContent="center" alignItems="center" style={{ cursor: 'pointer' }} fontSize="14px" onClick={() => setOpen(true)}>
        {title || 'Upload Custom Projections'}
        <Stack marginLeft="4px">
          <FileSVG />
        </Stack>
      </Stack>
    </>
  )
}

const ModalBox = (props: OwnProps) => {
  const [file, setFile] = React.useState<File>();
  const [fileName, setFileName] = React.useState<string>('');
  const [template, setTemplate] = React.useState<string>('');
  const [uploadErrors, setUploadErrors] = React.useState([]);
  const [customErrors, setCustomErrors] = React.useState('');
  const [customProjections, setCustomProjections] = React.useState<Projection[]>([]);
  const [customExposures, setCustomExposures] = React.useState<{ [key: string]: number }>({});
  const inputFile = React.useRef<HTMLInputElement | null>(null);
  const csvInstance = React.useRef<any | null>(null);
  const dispatch = useDispatch();
  const { slateId, onSetProj, selectedSlate } = props;
  const isShowdown = selectedSlate?.type?.toUpperCase() === 'SHOWDOWN';

  const fileReader = new FileReader();

  const handleOnChange = (e: any) => {
    setFile(e[0]);
  };

  const handleOnSubmit = () => {
    if (file) {
      fileReader.onload = async function async(event: any) {
        const projectionsData = event.target.result;
        const body = {
          projectionsData,
          slateId: slateId
        }
        const data = await fetch(`${process.env.REACT_APP_API_URL}/api/slateData/parseCustomProjections`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Accept-encoding': 'gzip, deflate',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        }).then(res => res.json());
        setCustomErrors(data?.errorMessage)
        setUploadErrors(data?.unmappedPlayers)
        setCustomProjections(data?.customProjections)

        if (data?.errorMessage === '') {
          const newCustomExposures: { [key: string]: number } = {};

          // For showdown slates, look for CPT and FLEX Max Exposure columns
          if (isShowdown) {
            const columns = ['Player Name and Id', 'CPT Max Exposure', 'FLEX Max Exposure'];
            const indexes: number[] = []
            // Trim all headers to remove \r and whitespace
            const columnsRowFromCSV: string[] = projectionsData.split('\n')[0].split(',').map(h => h.trim());

            columns.forEach(column => {
              indexes.push(columnsRowFromCSV.indexOf(column));
            });

            if (indexes.length === 3) {
              projectionsData.split('\n').forEach((row, index) => {
                const rowData = row.split(',');
                const playerName = rowData[indexes[0]];
                const cptExposureStr = rowData[indexes[1]]?.replace('\r', '');
                const flexExposureStr = rowData[indexes[2]]?.replace('\r', '');

                if (playerName) {
                  // Store CPT exposure with ||CPT suffix
                  const cptExposureNum = parseFloat(cptExposureStr);
                  if (!isNaN(cptExposureNum)) {
                    newCustomExposures[playerName + '||CPT'] = cptExposureNum;
                  }

                  // Store FLEX exposure with ||FLEX suffix
                  const flexExposureNum = parseFloat(flexExposureStr);
                  if (!isNaN(flexExposureNum)) {
                    newCustomExposures[playerName + '||FLEX'] = flexExposureNum;
                  }
                }
              })
            }
          } else {
            // For non-showdown slates, use single Max Exposure column
            const columns = ['Player Name and Id', 'Max Exposure\r'];
            const indexes: number[] = []
            const columnsRowFromCSV: string[] = projectionsData.split('\n')[0].split(',');

            columns.forEach(column => {
              indexes.push(columnsRowFromCSV.indexOf(column));
            });

            if (indexes.length === 2) {
              projectionsData.split('\n').forEach((row, index) => {
                const rowData = row.split(',');
                const playerName = rowData[indexes[0]];
                const exposureStr = rowData[indexes[1]]?.replace('\r', '');
                const exposureNum = parseFloat(exposureStr);
                if (playerName && !isNaN(exposureNum)) {
                  newCustomExposures[playerName] = exposureNum;
                }
              })
            }
          }

          setCustomExposures(newCustomExposures);
        }

        setFileName(file.name);
      };

      fileReader.readAsText(file);
    }
  };

  const onClear = () => {
    clearFileInput(document.getElementById('projectionsInput'))
    setUploadErrors([]);
    setCustomErrors('');
    setFileName("");
  }

  const getTemplate = async () => {
    const params = {
      slateId: slateId,
    }

    const data = await fetch(`${process.env.REACT_APP_API_URL}/api/contests/getProjectionsTemplate?` + new URLSearchParams(params)).then(res => res.json()).then(res => res.join('\n') || '');

    // adding the max exposure column to the data, doing it on the front end since it's really only for precontest
    const dataWithExposureColumns = data
      .split('\n')
      .map((line, index) => {
        if (index === 0) {
          // Header row - add appropriate exposure columns based on slate type
          if (isShowdown) {
            return `${line},CPT Max Exposure,FLEX Max Exposure`;
          }
          return `${line},Max Exposure`;
        }
        // Data rows - add default values
        if (isShowdown) {
          return `${line},100,100`;
        }
        return `${line},100`;
      })
      .join('\n');

    setTemplate(dataWithExposureColumns);
  }

  function clearFileInput(ctrl) {
    try {
      ctrl.value = null;
    } catch (ex) {
      Sentry.captureException(ex);
    }
    if (ctrl.value) {
      ctrl.parentNode.replaceChild(ctrl.cloneNode(true), ctrl);
    }
  }

  const submit = () => {
    // Merge maxExposure values into customProjections
    const projectionsWithExposure = customProjections.map(proj => {
      const exposure = customExposures[proj.nameAndId];
      if (exposure !== undefined && !isNaN(exposure)) {
        return {
          ...proj,
          maxExposure: exposure / 100 // Convert percentage to decimal
        };
      }
      return proj;
    });

    onSetProj(projectionsWithExposure);
    dispatch(setPlayerExposureCaps(customExposures));
  }

  React.useEffect(() => {
    if (file) {
      handleOnSubmit();
    }
  }, [file])

  // React.useEffect(() => {
  //   setFileName(defaultFileName);
  // }, [])

  React.useEffect(() => {
    if (template && csvInstance && csvInstance.current && csvInstance.current.link) {
      setTimeout(() => {
        csvInstance.current.link.click();
        setTemplate('');
      });
    }
  }, [template]);
  return (
    <Stack width="450px" direction="column" padding="4px 0px" borderRadius={"10px 10px 0px 0px"} boxShadow="0px -1px 0px 0px rgba(0, 0, 0, 0.12) inset" color="white" fontFamily="Inter" style={{ backgroundColor: '#010E20' }}>
      <Stack padding="8px 12px" direction="column">
        <Stack fontSize="14px" fontWeight={700} marginBottom="6px">Upload Custom Projections</Stack>
        <Stack fontSize="10px" fontWeight={400}>Use our template or upload your own CSV file with columns labeled 'Player Name,' 'Projection,' and 'Ownership' to integrate your projections.</Stack>
      </Stack>
      <Stack direction="row" justifyContent="center" sx={{
        "& button": { color: 'white', fontSize: '12px', textTransform: 'none', fontWeight: 500 },
        '& .outlined': {
          borderRadius: '6px',
          border: '1px solid rgba(255, 255, 255, 0.30)',
          padding: '6px 10px',
        }
      }}>
        <Button variant="text" endIcon={<GetAppIcon />} onClick={() => getTemplate()}>Download Template</Button>
        {
          template.length > 0 ? (
            <CSVLink
              data={template || ''}
              filename="projections-template.csv"
              target="_blank"
              asyncOnClick
              ref={csvInstance}
            >
            </CSVLink>
          ) : undefined
        }
        <Button variant="outlined" endIcon={<FileSVG />} className='outlined' onClick={() => inputFile?.current?.click()}>
          Upload CSV
          <input
            id='projectionsInput'
            type="file"
            hidden
            accept="text/csv"
            ref={inputFile}
            onChange={(e: any) => handleOnChange(e?.target?.files || {} as File[])}
          />
        </Button>
      </Stack>
      <Stack direction="row" justifyContent="center" alignItems="center" marginTop="16px" sx={{
        '& div:first-child': {
          backgroundColor: 'transparent !important',
          border: '1px solid rgba(255, 255, 255, 0.30) !important',
          borderRadius: 3,
          minHeight: '85px !important !important',
          width: '300px !important',
          '& svg': {
            color: 'white',
          }
        },
        '& .MuiDropzoneArea-text': {
          fontSize: '14pt !important',
          fontWeight: 700,
          display: 'flex !important',
          flexDirection: 'column !important',
          justifyContent: 'center !important',
          alignItems: 'center !important',
          '&::after': {
            fontSize: '12pt !important',
            color: 'rgba(100, 100, 100, .6) !important',
            content: "'or drag and drop it here'",
          }
        },
        '& .MuiDropzoneArea-icon': {
          display: 'none',
        },
      }}>
        <DropzoneArea
          onChange={(files) => handleOnChange(files)}
          showPreviewsInDropzone={false}
          acceptedFiles={["text/csv"]}
          dropzoneText={"Select a CSV file to upload"}
          Icon={undefined}
          filesLimit={1}
          showAlerts={false}
        />
      </Stack>
      <Stack direction="column" justifyContent="center" margin="8px 0" sx={{ '& svg': { marginRight: '4px', } }}>
        {fileName && (
          <Stack direction="row" justifyContent="center" alignItems="center" fontSize="10px" color='white' sx={{ cursor: 'pointer' }} onClick={() => onClear()}>
            {(uploadErrors.length === 0 && customErrors === '') && <CheckSVG />} {fileName} x
          </Stack>
        )}
        {
          customErrors !== '' ? (
            <Stack direction="row" alignItems="center" borderRadius="6px" border="1px solid #BA1B1B" fontSize="10px" padding="6px 10px" gap="10px" margin="8px 8px 0px 8px" sx={{
              '& svg': { color: '#BA1B1B' },
              '& button': {
                color: 'white',
                fontSize: '8px',
                fontWeight: 700,
                padding: '7px 8x',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.30)'
              }
            }}>
              <Stack><ErrorOutlineIcon /></Stack>
              <Stack direction="column">
                {customErrors}
              </Stack>
            </Stack>
          ) :
            (
              <>
                {uploadErrors.length > 0 && (
                  <Stack direction="row" alignItems="center" borderRadius="6px" border="1px solid #BA1B1B" fontSize="10px" padding="6px 10px" gap="10px" margin="8px 8px 0px 8px" sx={{
                    '& svg': { color: '#BA1B1B' },
                    '& button': {
                      color: 'white',
                      fontSize: '8px',
                      fontWeight: 700,
                      padding: '7px 8x',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.30)'
                    }
                  }}>
                    <Stack><ErrorOutlineIcon /></Stack>
                    <Stack direction="column" maxWidth="262px">
                      <Stack>
                        We couldn't match the following players
                      </Stack>
                      <Stack sx={{ '& ul': { paddingLeft: '20px', margin: '4px 0' } }} maxHeight="35px" overflow="auto">
                        <ul>
                          {
                            uploadErrors.map(x => <li>{x}</li>)
                          }
                        </ul>
                      </Stack>
                      <Stack>
                        Ignore and Upload to use the default projection for the player(s) listed or fix your file and re-upload
                      </Stack>
                    </Stack>
                    <Stack>
                      <Button onClick={() => submit()}>IGNORE AND UPLOAD</Button>
                    </Stack>
                  </Stack>
                )}
              </>
            )
        }
        {

        }
      </Stack>
      <Stack marginTop="8px" height="2px" width="100%" style={{ backgroundColor: 'rgba(251, 255, 57, 0.30)' }} />
      <Stack direction="row" justifyContent="end" padding="8px" sx={{
        "& button": { color: 'white', fontSize: '12px', fontWeight: 700, backgroundColor: '#015ACE', borderRadius: '6px', padding: '6px 12px', '&:hover': { backgroundColor: '#015ACE' } },
      }}>
        <Button disabled={uploadErrors.length > 0} onClick={() => submit()}>UPLOAD</Button>
      </Stack>
    </Stack>
  )
}

const FileSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" viewBox="0 0 12 14" fill="none">
    <path d="M5.53366 8.28337V11.1334C5.53366 11.2778 5.58088 11.3973 5.67533 11.4917C5.76977 11.5862 5.88921 11.6334 6.03366 11.6334C6.1781 11.6334 6.29755 11.5862 6.39199 11.4917C6.48644 11.3973 6.53366 11.2778 6.53366 11.1334V8.28337L7.55032 9.30004C7.58366 9.33337 7.66699 9.32226 7.80032 9.26671C7.93366 9.21115 8.05866 9.16115 8.17532 9.11671C8.29199 9.07226 8.37255 9.05282 8.41699 9.05837C8.46144 9.06393 8.40588 9.14448 8.25033 9.30004C8.35033 9.20004 8.39755 9.08337 8.39199 8.95004C8.38644 8.81671 8.33366 8.70004 8.23366 8.60004L6.35033 6.76671C6.29477 6.71115 6.23921 6.67226 6.18366 6.65004C6.1281 6.62782 6.06699 6.61671 6.00033 6.61671C5.93366 6.61671 5.87255 6.62782 5.81699 6.65004C5.76144 6.67226 5.70588 6.71115 5.65033 6.76671L3.78366 8.63337C3.68366 8.73337 3.63366 8.85004 3.63366 8.98337C3.63366 9.11671 3.68366 9.23337 3.78366 9.33337C3.88366 9.43337 4.00033 9.48337 4.13366 9.48337C4.26699 9.48337 4.38366 9.43337 4.48366 9.33337L5.53366 8.28337ZM1.66699 13.6667C1.40033 13.6667 1.16699 13.5667 0.966992 13.3667C0.766992 13.1667 0.666992 12.9334 0.666992 12.6667V1.33337C0.666992 1.06671 0.766992 0.833374 0.966992 0.633374C1.16699 0.433374 1.40033 0.333374 1.66699 0.333374H7.26699C7.40033 0.333374 7.53088 0.361152 7.65866 0.416707C7.78644 0.472263 7.89477 0.544485 7.98366 0.633374L11.0337 3.68337C11.1225 3.77226 11.1948 3.8806 11.2503 4.00837C11.3059 4.13615 11.3337 4.26671 11.3337 4.40004V12.6667C11.3337 12.9334 11.2337 13.1667 11.0337 13.3667C10.8337 13.5667 10.6003 13.6667 10.3337 13.6667H1.66699ZM7.18366 3.93337C7.18366 4.07782 7.23088 4.19726 7.32533 4.29171C7.41977 4.38615 7.53921 4.43337 7.68366 4.43337H10.3337L7.18366 1.33337V3.93337Z" fill="white" />
  </svg>
);

const CheckSVG = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="17" height="18" viewBox="0 0 17 18" fill="none">
    <path d="M12.3253 5.31663L6.83366 10.8083L3.84199 7.82496L2.66699 8.99996L6.83366 13.1666L13.5003 6.49996L12.3253 5.31663ZM8.50033 0.666626C3.90033 0.666626 0.166992 4.39996 0.166992 8.99996C0.166992 13.6 3.90033 17.3333 8.50033 17.3333C13.1003 17.3333 16.8337 13.6 16.8337 8.99996C16.8337 4.39996 13.1003 0.666626 8.50033 0.666626ZM8.50033 15.6666C4.81699 15.6666 1.83366 12.6833 1.83366 8.99996C1.83366 5.31663 4.81699 2.33329 8.50033 2.33329C12.1837 2.33329 15.167 5.31663 15.167 8.99996C15.167 12.6833 12.1837 15.6666 8.50033 15.6666Z" fill="#368D39" />
  </svg>
)

export default ProjectionModal